/**
 * Tracking Queue System with Batch Support
 * Gom nhiều tracking events lại và gửi theo batch để tối ưu performance
 */

import axios from 'axios';

export interface TrackingEvent {
  eventType: string;
  eventData: any;
  timestamp: string;
  sessionId?: string;
}

export interface BatchTrackingPayload {
  events: TrackingEvent[];
  metadata: {
    batchId: string;
    batchSize: number;
    batchTimestamp: string;
  };
}

export interface TrackingQueueConfig {
  maxQueueSize?: number; // Max events before auto-flush
  flushInterval?: number; // Auto-flush interval in ms
  maxRetries?: number; // Max retry attempts
  retryDelay?: number; // Delay between retries in ms
  endpoint: string;
  onSuccess?: (batch: BatchTrackingPayload) => void;
  onError?: (error: Error, batch: BatchTrackingPayload) => void;
}

class TrackingQueue {
  private queue: TrackingEvent[] = [];
  private config: Required<TrackingQueueConfig>;
  private flushTimer: NodeJS.Timeout | null = null;
  private isProcessing = false;
  private failedBatches: BatchTrackingPayload[] = [];
  private sessionId: string;

  constructor(config: TrackingQueueConfig) {
    this.config = {
      maxQueueSize: config.maxQueueSize || 10,
      flushInterval: config.flushInterval || 10000, // 10 seconds
      maxRetries: config.maxRetries || 3,
      retryDelay: config.retryDelay || 2000, // 2 seconds
      endpoint: config.endpoint,
      onSuccess: config.onSuccess || (() => {}),
      onError: config.onError || (() => {}),
    };

    this.sessionId = this.generateSessionId();
    this.startAutoFlush();
    this.setupBeforeUnload();
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique batch ID
   */
  private generateBatchId(): string {
    return `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add event to queue
   */
  public track(eventType: string, eventData: any): void {
    const event: TrackingEvent = {
      eventType,
      eventData,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
    };

    this.queue.push(event);

    // Auto-flush if queue size exceeds limit
    if (this.queue.length >= this.config.maxQueueSize) {
      this.flush();
    }
  }

  /**
   * Flush queue immediately
   */
  public async flush(): Promise<void> {
    if (this.queue.length === 0 || this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    const batch: BatchTrackingPayload = {
      events: [...this.queue],
      metadata: {
        batchId: this.generateBatchId(),
        batchSize: this.queue.length,
        batchTimestamp: new Date().toISOString(),
      },
    };

    // Clear queue
    this.queue = [];

    try {
      await this.sendBatch(batch);
      this.config.onSuccess?.(batch);
    } catch (error) {
      console.error('Failed to send tracking batch:', error);
      this.failedBatches.push(batch);
      this.config.onError?.(error as Error, batch);

      // Retry failed batches
      this.retryFailedBatches();
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Send batch to backend
   */
  private async sendBatch(batch: BatchTrackingPayload, retryCount = 0): Promise<void> {
    try {
      await axios.post(this.config.endpoint, batch, {
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
        timeout: 10000, // 10 second timeout
      });
    } catch (error) {
      if (retryCount < this.config.maxRetries) {
        // Exponential backoff
        const delay = this.config.retryDelay * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.sendBatch(batch, retryCount + 1);
      }
      throw error;
    }
  }

  /**
   * Retry failed batches
   */
  private async retryFailedBatches(): Promise<void> {
    if (this.failedBatches.length === 0) return;

    const batch = this.failedBatches.shift();
    if (!batch) return;

    try {
      await this.sendBatch(batch);
      this.config.onSuccess?.(batch);
    } catch (error) {
      console.error('Failed to retry batch:', error);
      // Don't re-add to failed batches to prevent infinite loop
    }
  }

  /**
   * Start auto-flush timer
   */
  private startAutoFlush(): void {
    this.flushTimer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.config.flushInterval);
  }

  /**
   * Stop auto-flush timer
   */
  private stopAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Setup beforeunload handler to flush remaining events
   */
  private setupBeforeUnload(): void {
    window.addEventListener('beforeunload', () => {
      this.flushSync();
    });

    // Also flush on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushSync();
      }
    });
  }

  /**
   * Synchronous flush using sendBeacon for page unload
   */
  private flushSync(): void {
    if (this.queue.length === 0) return;

    const batch: BatchTrackingPayload = {
      events: [...this.queue],
      metadata: {
        batchId: this.generateBatchId(),
        batchSize: this.queue.length,
        batchTimestamp: new Date().toISOString(),
      },
    };

    // Use sendBeacon for reliable delivery on page unload
    const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
    navigator.sendBeacon(this.config.endpoint, blob);

    this.queue = [];
  }

  /**
   * Get current queue size
   */
  public getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Get failed batches count
   */
  public getFailedBatchesCount(): number {
    return this.failedBatches.length;
  }

  /**
   * Clear queue
   */
  public clear(): void {
    this.queue = [];
    this.failedBatches = [];
  }

  /**
   * Destroy queue and cleanup
   */
  public destroy(): void {
    this.stopAutoFlush();
    this.flush();
    this.clear();
  }
}

// Singleton instance with default config
let defaultQueue: TrackingQueue | null = null;

/**
 * Initialize default tracking queue
 */
export const initTrackingQueue = (config: TrackingQueueConfig): TrackingQueue => {
  if (defaultQueue) {
    defaultQueue.destroy();
  }
  defaultQueue = new TrackingQueue(config);
  return defaultQueue;
};

/**
 * Get default tracking queue
 */
export const getTrackingQueue = (): TrackingQueue | null => {
  return defaultQueue;
};

/**
 * Quick track method using default queue
 */
export const track = (eventType: string, eventData: any): void => {
  if (!defaultQueue) {
    console.warn('Tracking queue not initialized. Call initTrackingQueue() first.');
    return;
  }
  defaultQueue.track(eventType, eventData);
};

/**
 * Quick flush method using default queue
 */
export const flush = async (): Promise<void> => {
  if (!defaultQueue) {
    console.warn('Tracking queue not initialized.');
    return;
  }
  await defaultQueue.flush();
};

export default TrackingQueue;
